import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Separator } from "../../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { 
  Building2, Save, User, Users, MapPin, Calendar, Euro, FileText,
  Plus, Trash2, Edit2, CheckCircle2, AlertCircle, Loader2, BadgeCheck
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Formes juridiques françaises
const FORMES_JURIDIQUES = [
  { value: "SAS", label: "SAS - Société par Actions Simplifiée" },
  { value: "SASU", label: "SASU - SAS Unipersonnelle" },
  { value: "SARL", label: "SARL - Société à Responsabilité Limitée" },
  { value: "EURL", label: "EURL - Entreprise Unipersonnelle à Responsabilité Limitée" },
  { value: "SA", label: "SA - Société Anonyme" },
  { value: "SCI", label: "SCI - Société Civile Immobilière" },
  { value: "SNC", label: "SNC - Société en Nom Collectif" },
  { value: "SELARL", label: "SELARL - Société d'Exercice Libéral" },
];

// Fonctions dirigeant
const FONCTIONS_DIRIGEANT = [
  { value: "Président", label: "Président" },
  { value: "Directeur Général", label: "Directeur Général" },
  { value: "Gérant", label: "Gérant" },
  { value: "Co-gérant", label: "Co-gérant" },
  { value: "Administrateur", label: "Administrateur" },
];

// French month names for parsing
const FRENCH_MONTHS = {
  "janvier": "01", "février": "02", "fevrier": "02", "mars": "03", "avril": "04",
  "mai": "05", "juin": "06", "juillet": "07", "août": "08", "aout": "08",
  "septembre": "09", "octobre": "10", "novembre": "11", "décembre": "12", "decembre": "12"
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  
  // Handle ISO format (2020-01-07 or 2020-01-07T00:00:00)
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
    return dateStr.split("T")[0];
  }
  
  // Handle French text format (07 janvier 2020)
  const frenchMatch = dateStr.match(/(\d{1,2})\s+(\w+)\s+(\d{4})/);
  if (frenchMatch) {
    const day = frenchMatch[1].padStart(2, '0');
    const monthName = frenchMatch[2].toLowerCase();
    const year = frenchMatch[3];
    const month = FRENCH_MONTHS[monthName];
    if (month) {
      return `${year}-${month}-${day}`;
    }
  }
  
  // Handle DD/MM/YYYY format
  const dmyMatch = dateStr.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (dmyMatch) {
    return `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;
  }
  
  return dateStr;
};

export default function ProfilSociete() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("identite");
  
  // Modal states
  const [showDirigeantModal, setShowDirigeantModal] = useState(false);
  const [showAssocieModal, setShowAssocieModal] = useState(false);
  const [editingDirigeant, setEditingDirigeant] = useState(null);
  const [editingAssocie, setEditingAssocie] = useState(null);

  // Fetch profile
  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/corporate/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data);
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Erreur lors du chargement du profil");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Update field
  const updateField = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  // Update nested field (siege_social)
  const updateSiegeSocial = (field, value) => {
    setProfile(prev => ({
      ...prev,
      siege_social: { ...prev.siege_social, [field]: value }
    }));
  };

  // Save profile
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/corporate/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profil société enregistré");
      fetchProfile(); // Refresh to get updated is_complete status
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Add/Edit Dirigeant
  const handleSaveDirigeant = (dirigeant) => {
    const dirigeants = [...(profile.dirigeants || [])];
    
    if (editingDirigeant) {
      // Edit existing
      const index = dirigeants.findIndex(d => d.id === editingDirigeant.id);
      if (index !== -1) {
        dirigeants[index] = { ...editingDirigeant, ...dirigeant };
      }
    } else {
      // Add new
      dirigeants.push({
        id: crypto.randomUUID(),
        ...dirigeant,
        est_actif: true
      });
    }
    
    setProfile(prev => ({ ...prev, dirigeants }));
    setShowDirigeantModal(false);
    setEditingDirigeant(null);
  };

  // Remove Dirigeant
  const handleRemoveDirigeant = (id) => {
    setProfile(prev => ({
      ...prev,
      dirigeants: prev.dirigeants.filter(d => d.id !== id)
    }));
  };

  // Add/Edit Associe
  const handleSaveAssocie = (associe) => {
    const associes = [...(profile.associes || [])];
    
    if (editingAssocie) {
      const index = associes.findIndex(a => a.id === editingAssocie.id);
      if (index !== -1) {
        associes[index] = { ...editingAssocie, ...associe };
      }
    } else {
      associes.push({
        id: crypto.randomUUID(),
        ...associe
      });
    }
    
    // Recalculate percentages
    const totalParts = associes.reduce((sum, a) => sum + (a.nombre_parts || 0), 0);
    associes.forEach(a => {
      a.pourcentage = totalParts > 0 ? ((a.nombre_parts / totalParts) * 100).toFixed(2) : 0;
    });
    
    setProfile(prev => ({ ...prev, associes }));
    setShowAssocieModal(false);
    setEditingAssocie(null);
  };

  // Remove Associe
  const handleRemoveAssocie = (id) => {
    const associes = profile.associes.filter(a => a.id !== id);
    
    // Recalculate percentages
    const totalParts = associes.reduce((sum, a) => sum + (a.nombre_parts || 0), 0);
    associes.forEach(a => {
      a.pourcentage = totalParts > 0 ? ((a.nombre_parts / totalParts) * 100).toFixed(2) : 0;
    });
    
    setProfile(prev => ({ ...prev, associes }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with status */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-100 rounded-xl">
            <Building2 className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {profile?.raison_sociale || "Profil Société"}
            </h2>
            <p className="text-sm text-gray-500">
              {profile?.forme_juridique} {profile?.siren && `• SIREN ${profile.siren}`}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {profile?.is_complete ? (
            <Badge className="bg-green-100 text-green-700 gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Profil complet
            </Badge>
          ) : (
            <Badge variant="outline" className="text-orange-600 border-orange-300 gap-1">
              <AlertCircle className="w-3 h-3" />
              Profil incomplet
            </Badge>
          )}
          
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="gap-2"
            data-testid="save-profile-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="identite" className="gap-2">
            <Building2 className="w-4 h-4 hidden sm:block" />
            Identité
          </TabsTrigger>
          <TabsTrigger value="capital" className="gap-2">
            <Euro className="w-4 h-4 hidden sm:block" />
            Capital
          </TabsTrigger>
          <TabsTrigger value="dirigeants" className="gap-2">
            <User className="w-4 h-4 hidden sm:block" />
            Dirigeants
          </TabsTrigger>
          <TabsTrigger value="associes" className="gap-2">
            <Users className="w-4 h-4 hidden sm:block" />
            Associés
          </TabsTrigger>
        </TabsList>

        {/* Tab: Identité */}
        <TabsContent value="identite" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informations légales</CardTitle>
              <CardDescription>Raison sociale et immatriculation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="raison_sociale">Raison sociale *</Label>
                  <Input
                    id="raison_sociale"
                    value={profile?.raison_sociale || ""}
                    onChange={(e) => updateField("raison_sociale", e.target.value)}
                    placeholder="BE-TAYE"
                    data-testid="raison-sociale-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="sigle">Sigle (optionnel)</Label>
                  <Input
                    id="sigle"
                    value={profile?.sigle || ""}
                    onChange={(e) => updateField("sigle", e.target.value)}
                    placeholder="BT"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="forme_juridique">Forme juridique *</Label>
                  <Select 
                    value={profile?.forme_juridique || "SAS"} 
                    onValueChange={(v) => updateField("forme_juridique", v)}
                  >
                    <SelectTrigger id="forme_juridique" data-testid="forme-juridique-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FORMES_JURIDIQUES.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="siren">SIREN *</Label>
                  <Input
                    id="siren"
                    value={profile?.siren || ""}
                    onChange={(e) => updateField("siren", e.target.value.replace(/\D/g, "").slice(0, 9))}
                    placeholder="123456789"
                    maxLength={9}
                    data-testid="siren-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="siret">SIRET</Label>
                  <Input
                    id="siret"
                    value={profile?.siret || ""}
                    onChange={(e) => updateField("siret", e.target.value.replace(/\D/g, "").slice(0, 14))}
                    placeholder="12345678900001"
                    maxLength={14}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="rcs_ville">RCS Ville</Label>
                  <Input
                    id="rcs_ville"
                    value={profile?.rcs_ville || ""}
                    onChange={(e) => updateField("rcs_ville", e.target.value)}
                    placeholder="Paris"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tva_intracommunautaire">TVA Intracommunautaire</Label>
                  <Input
                    id="tva_intracommunautaire"
                    value={profile?.tva_intracommunautaire || ""}
                    onChange={(e) => updateField("tva_intracommunautaire", e.target.value.toUpperCase())}
                    placeholder="FR12345678901"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="code_ape">Code APE/NAF</Label>
                  <Input
                    id="code_ape"
                    value={profile?.code_ape || ""}
                    onChange={(e) => updateField("code_ape", e.target.value.toUpperCase())}
                    placeholder="6202A"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Siège Social */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-gray-500" />
                Siège social
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="adresse">Adresse *</Label>
                  <Input
                    id="adresse"
                    value={profile?.siege_social?.adresse || ""}
                    onChange={(e) => updateSiegeSocial("adresse", e.target.value)}
                    placeholder="123 rue de la Paix"
                    data-testid="adresse-input"
                  />
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="complement">Complément d'adresse</Label>
                  <Input
                    id="complement"
                    value={profile?.siege_social?.complement || ""}
                    onChange={(e) => updateSiegeSocial("complement", e.target.value)}
                    placeholder="Bâtiment A, 3ème étage"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="code_postal">Code postal *</Label>
                  <Input
                    id="code_postal"
                    value={profile?.siege_social?.code_postal || ""}
                    onChange={(e) => updateSiegeSocial("code_postal", e.target.value.replace(/\D/g, "").slice(0, 5))}
                    placeholder="75001"
                    maxLength={5}
                    data-testid="code-postal-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="ville">Ville *</Label>
                  <Input
                    id="ville"
                    value={profile?.siege_social?.ville || ""}
                    onChange={(e) => updateSiegeSocial("ville", e.target.value)}
                    placeholder="Paris"
                    data-testid="ville-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="pays">Pays</Label>
                  <Input
                    id="pays"
                    value={profile?.siege_social?.pays || "France"}
                    onChange={(e) => updateSiegeSocial("pays", e.target.value)}
                    placeholder="France"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dates importantes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-500" />
                Dates importantes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_creation">Date de création</Label>
                  <Input
                    id="date_creation"
                    type="date"
                    value={formatDate(profile?.date_creation) || ""}
                    onChange={(e) => updateField("date_creation", e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="date_immatriculation">Date d'immatriculation RCS</Label>
                  <Input
                    id="date_immatriculation"
                    type="date"
                    value={formatDate(profile?.date_immatriculation) || ""}
                    onChange={(e) => updateField("date_immatriculation", e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="date_cloture_exercice">Clôture exercice (MM-JJ) *</Label>
                  <Input
                    id="date_cloture_exercice"
                    value={profile?.date_cloture_exercice || "12-31"}
                    onChange={(e) => updateField("date_cloture_exercice", e.target.value)}
                    placeholder="12-31"
                    maxLength={5}
                    data-testid="date-cloture-input"
                  />
                  <p className="text-xs text-gray-500">Format: MM-JJ (ex: 12-31 pour le 31 décembre)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Capital */}
        <TabsContent value="capital" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Euro className="w-5 h-5 text-gray-500" />
                Capital social
              </CardTitle>
              <CardDescription>Montant et répartition du capital</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="capital_social">Capital social *</Label>
                  <div className="relative">
                    <Input
                      id="capital_social"
                      type="number"
                      value={profile?.capital_social || ""}
                      onChange={(e) => updateField("capital_social", parseFloat(e.target.value) || 0)}
                      placeholder="10000"
                      className="pr-10"
                      data-testid="capital-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="devise">Devise</Label>
                  <Select 
                    value={profile?.devise || "EUR"} 
                    onValueChange={(v) => updateField("devise", v)}
                  >
                    <SelectTrigger id="devise">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="USD">USD - Dollar US</SelectItem>
                      <SelectItem value="GBP">GBP - Livre Sterling</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="nombre_parts">Nombre de parts/actions</Label>
                  <Input
                    id="nombre_parts"
                    type="number"
                    value={profile?.nombre_parts || ""}
                    onChange={(e) => updateField("nombre_parts", parseInt(e.target.value) || 0)}
                    placeholder="1000"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="valeur_nominale">Valeur nominale</Label>
                  <div className="relative">
                    <Input
                      id="valeur_nominale"
                      type="number"
                      step="0.01"
                      value={profile?.valeur_nominale || ""}
                      onChange={(e) => updateField("valeur_nominale", parseFloat(e.target.value) || 0)}
                      placeholder="10"
                      className="pr-10"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                  </div>
                </div>
              </div>
              
              {/* Capital summary */}
              {profile?.capital_social > 0 && profile?.nombre_parts > 0 && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">{profile.nombre_parts.toLocaleString("fr-FR")}</span> parts de{" "}
                    <span className="font-semibold">{(profile.capital_social / profile.nombre_parts).toFixed(2)} €</span> chacune
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Dirigeants */}
        <TabsContent value="dirigeants" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-gray-500" />
                  Dirigeants
                </CardTitle>
                <CardDescription>Président, Directeur Général, Gérant...</CardDescription>
              </div>
              <Button 
                size="sm" 
                onClick={() => {
                  setEditingDirigeant(null);
                  setShowDirigeantModal(true);
                }}
                className="gap-2"
                data-testid="add-dirigeant-btn"
              >
                <Plus className="w-4 h-4" />
                Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              {(!profile?.dirigeants || profile.dirigeants.length === 0) ? (
                <div className="text-center py-8 text-gray-500">
                  <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Aucun dirigeant enregistré</p>
                  <p className="text-sm">Ajoutez au moins un dirigeant pour compléter le profil</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {profile.dirigeants.map((dirigeant) => (
                    <div 
                      key={dirigeant.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border"
                      data-testid={`dirigeant-${dirigeant.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {dirigeant.civilite} {dirigeant.prenom} {dirigeant.nom}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Badge variant="outline">{dirigeant.fonction}</Badge>
                            {dirigeant.date_nomination && (
                              <span>depuis le {new Date(dirigeant.date_nomination).toLocaleDateString("fr-FR")}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingDirigeant(dirigeant);
                            setShowDirigeantModal(true);
                          }}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveDirigeant(dirigeant.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Associés */}
        <TabsContent value="associes" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-500" />
                  Associés / Actionnaires
                </CardTitle>
                <CardDescription>Répartition du capital entre les associés</CardDescription>
              </div>
              <Button 
                size="sm" 
                onClick={() => {
                  setEditingAssocie(null);
                  setShowAssocieModal(true);
                }}
                className="gap-2"
                data-testid="add-associe-btn"
              >
                <Plus className="w-4 h-4" />
                Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              {(!profile?.associes || profile.associes.length === 0) ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Aucun associé enregistré</p>
                  <p className="text-sm">Ajoutez au moins un associé pour compléter le profil</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {profile.associes.map((associe) => (
                    <div 
                      key={associe.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border"
                      data-testid={`associe-${associe.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          {associe.type === "personne_morale" ? (
                            <Building2 className="w-5 h-5 text-purple-600" />
                          ) : (
                            <User className="w-5 h-5 text-purple-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {associe.type === "personne_morale" 
                              ? associe.denomination 
                              : `${associe.civilite || ""} ${associe.prenom || ""} ${associe.nom || ""}`
                            }
                          </p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <span className="font-medium text-purple-600">{associe.pourcentage}%</span>
                            <span>•</span>
                            <span>{associe.nombre_parts?.toLocaleString("fr-FR")} parts</span>
                            {associe.type === "personne_morale" && (
                              <>
                                <span>•</span>
                                <Badge variant="outline" className="text-xs">Personne morale</Badge>
                              </>
                            )}
                          </div>
                          {/* Show address if available */}
                          {associe.adresse && (
                            <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {associe.adresse}, {associe.code_postal} {associe.ville}
                            </p>
                          )}
                          {!associe.adresse && (
                            <p className="text-xs text-orange-500 flex items-center gap-1 mt-1">
                              <AlertCircle className="w-3 h-3" />
                              Adresse non renseignée
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingAssocie(associe);
                            setShowAssocieModal(true);
                          }}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveAssocie(associe.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Total summary */}
                  <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-purple-800">Total</span>
                      <span className="font-bold text-purple-900">
                        {profile.associes.reduce((sum, a) => sum + (a.nombre_parts || 0), 0).toLocaleString("fr-FR")} parts
                        {" "}({profile.associes.reduce((sum, a) => sum + parseFloat(a.pourcentage || 0), 0).toFixed(2)}%)
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dirigeant Modal */}
      <DirigeantModal
        open={showDirigeantModal}
        onOpenChange={setShowDirigeantModal}
        dirigeant={editingDirigeant}
        onSave={handleSaveDirigeant}
      />

      {/* Associe Modal */}
      <AssocieModal
        open={showAssocieModal}
        onOpenChange={setShowAssocieModal}
        associe={editingAssocie}
        totalParts={profile?.nombre_parts || 0}
        onSave={handleSaveAssocie}
      />
    </div>
  );
}

// Dirigeant Modal Component
function DirigeantModal({ open, onOpenChange, dirigeant, onSave }) {
  const [form, setForm] = useState({
    civilite: "M.",
    nom: "",
    prenom: "",
    fonction: "Président",
    date_nomination: "",
    date_fin_mandat: ""
  });

  useEffect(() => {
    if (dirigeant) {
      setForm({
        civilite: dirigeant.civilite || "M.",
        nom: dirigeant.nom || "",
        prenom: dirigeant.prenom || "",
        fonction: dirigeant.fonction || "Président",
        date_nomination: formatDate(dirigeant.date_nomination) || "",
        date_fin_mandat: formatDate(dirigeant.date_fin_mandat) || ""
      });
    } else {
      setForm({
        civilite: "M.",
        nom: "",
        prenom: "",
        fonction: "Président",
        date_nomination: "",
        date_fin_mandat: ""
      });
    }
  }, [dirigeant, open]);

  const handleSubmit = () => {
    if (!form.nom || !form.prenom) {
      toast.error("Veuillez remplir le nom et le prénom");
      return;
    }
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{dirigeant ? "Modifier le dirigeant" : "Ajouter un dirigeant"}</DialogTitle>
          <DialogDescription>Informations du représentant légal</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Civilité</Label>
              <Select value={form.civilite} onValueChange={(v) => setForm(f => ({ ...f, civilite: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="M.">M.</SelectItem>
                  <SelectItem value="Mme">Mme</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Prénom *</Label>
              <Input
                value={form.prenom}
                onChange={(e) => setForm(f => ({ ...f, prenom: e.target.value }))}
                placeholder="Jean"
                data-testid="dirigeant-prenom-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input
                value={form.nom}
                onChange={(e) => setForm(f => ({ ...f, nom: e.target.value }))}
                placeholder="DUPONT"
                data-testid="dirigeant-nom-input"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Fonction</Label>
            <Select value={form.fonction} onValueChange={(v) => setForm(f => ({ ...f, fonction: v }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FONCTIONS_DIRIGEANT.map(f => (
                  <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date de nomination</Label>
              <Input
                type="date"
                value={form.date_nomination}
                onChange={(e) => setForm(f => ({ ...f, date_nomination: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Fin de mandat (optionnel)</Label>
              <Input
                type="date"
                value={form.date_fin_mandat}
                onChange={(e) => setForm(f => ({ ...f, date_fin_mandat: e.target.value }))}
              />
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleSubmit} data-testid="save-dirigeant-btn">
            {dirigeant ? "Modifier" : "Ajouter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Associe Modal Component
function AssocieModal({ open, onOpenChange, associe, totalParts, onSave }) {
  const [form, setForm] = useState({
    type: "personne_physique",
    civilite: "M.",
    nom: "",
    prenom: "",
    denomination: "",
    siren_associe: "",
    representant: "",
    nombre_parts: 0,
    date_entree: "",
    // Adresse fields
    adresse: "",
    code_postal: "",
    ville: ""
  });

  useEffect(() => {
    if (associe) {
      setForm({
        type: associe.type || "personne_physique",
        civilite: associe.civilite || "M.",
        nom: associe.nom || "",
        prenom: associe.prenom || "",
        denomination: associe.denomination || "",
        siren_associe: associe.siren_associe || "",
        representant: associe.representant || "",
        nombre_parts: associe.nombre_parts || 0,
        date_entree: formatDate(associe.date_entree) || "",
        adresse: associe.adresse || "",
        code_postal: associe.code_postal || "",
        ville: associe.ville || ""
      });
    } else {
      setForm({
        type: "personne_physique",
        civilite: "M.",
        nom: "",
        prenom: "",
        denomination: "",
        siren_associe: "",
        representant: "",
        nombre_parts: 0,
        date_entree: "",
        adresse: "",
        code_postal: "",
        ville: ""
      });
    }
  }, [associe, open]);

  const handleSubmit = () => {
    if (form.type === "personne_physique" && (!form.nom || !form.prenom)) {
      toast.error("Veuillez remplir le nom et le prénom");
      return;
    }
    if (form.type === "personne_morale" && !form.denomination) {
      toast.error("Veuillez remplir la dénomination sociale");
      return;
    }
    if (form.nombre_parts <= 0) {
      toast.error("Le nombre de parts doit être supérieur à 0");
      return;
    }
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{associe ? "Modifier l'associé" : "Ajouter un associé"}</DialogTitle>
          <DialogDescription>Informations de l'actionnaire ou associé</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Type d'associé</Label>
            <Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}>
              <SelectTrigger data-testid="associe-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personne_physique">Personne physique</SelectItem>
                <SelectItem value="personne_morale">Personne morale (société)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {form.type === "personne_physique" ? (
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Civilité</Label>
                <Select value={form.civilite} onValueChange={(v) => setForm(f => ({ ...f, civilite: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M.">M.</SelectItem>
                    <SelectItem value="Mme">Mme</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Prénom *</Label>
                <Input
                  value={form.prenom}
                  onChange={(e) => setForm(f => ({ ...f, prenom: e.target.value }))}
                  placeholder="Jean"
                  data-testid="associe-prenom-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={form.nom}
                  onChange={(e) => setForm(f => ({ ...f, nom: e.target.value }))}
                  placeholder="DUPONT"
                  data-testid="associe-nom-input"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Dénomination sociale *</Label>
                <Input
                  value={form.denomination}
                  onChange={(e) => setForm(f => ({ ...f, denomination: e.target.value }))}
                  placeholder="Holding ABC SAS"
                  data-testid="associe-denomination-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SIREN</Label>
                  <Input
                    value={form.siren_associe}
                    onChange={(e) => setForm(f => ({ ...f, siren_associe: e.target.value.replace(/\D/g, "").slice(0, 9) }))}
                    placeholder="123456789"
                    maxLength={9}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Représentant légal</Label>
                  <Input
                    value={form.representant}
                    onChange={(e) => setForm(f => ({ ...f, representant: e.target.value }))}
                    placeholder="M. Jean DUPONT"
                  />
                </div>
              </div>
            </div>
          )}
          
          <Separator />
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nombre de parts *</Label>
              <Input
                type="number"
                value={form.nombre_parts}
                onChange={(e) => setForm(f => ({ ...f, nombre_parts: parseInt(e.target.value) || 0 }))}
                placeholder="100"
                data-testid="associe-parts-input"
              />
              {totalParts > 0 && form.nombre_parts > 0 && (
                <p className="text-xs text-gray-500">
                  = {((form.nombre_parts / totalParts) * 100).toFixed(2)}% du capital
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Date d'entrée</Label>
              <Input
                type="date"
                value={form.date_entree}
                onChange={(e) => setForm(f => ({ ...f, date_entree: e.target.value }))}
              />
            </div>
          </div>
          
          <Separator />
          
          {/* Adresse section */}
          <div className="space-y-4">
            <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Adresse (pour convocations)
            </Label>
            
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                value={form.adresse}
                onChange={(e) => setForm(f => ({ ...f, adresse: e.target.value }))}
                placeholder="123 rue de la Paix"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code postal</Label>
                <Input
                  value={form.code_postal}
                  onChange={(e) => setForm(f => ({ ...f, code_postal: e.target.value.replace(/\D/g, "").slice(0, 5) }))}
                  placeholder="75001"
                  maxLength={5}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Ville</Label>
                <Input
                  value={form.ville}
                  onChange={(e) => setForm(f => ({ ...f, ville: e.target.value }))}
                  placeholder="Paris"
                />
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleSubmit} data-testid="save-associe-btn">
            {associe ? "Modifier" : "Ajouter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
